/**
 * MarginHistoryChart — "¿cómo viene mi mes?" en /industry.
 *
 * Muestra total_margin por día para el rango seleccionado (7d / 30d / 90d).
 * El backend hace forward-fill de días sin data — los puntos sintéticos se
 * dibujan con opacity reducida para que se distingan de los snapshots reales.
 *
 *  - SVG inline, sin libs (~100 LOC). Mismo pattern que WalletSparkline.
 *  - Color de la línea: verde si último real > 0, rojo si < 0, gris si 0.
 *  - Línea base en Y=0 si hay valores negativos en el rango.
 *  - Hover: tooltip con fecha + margin + estado (real / forward-filled).
 *  - Snapshot manual button: POST /api/industry/margin-history/snapshot.
 *
 * Caveats:
 *  - Con < 1 snapshot real, el chart es útil pero todos los puntos son
 *    forward-filled (mostrarlo igual ayuda a sembrar la feature).
 *  - El cron diario (wrangler.toml: `0 4 * * *`) cubre 1 user/día
 *    round-robin, así que con 1 user los snapshots son diarios reales.
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { CHART_COLORS } from '../lib/chartColors'

type Period = 7 | 30 | 90

type Snapshot = {
  date: string
  total_margin: number
  marginable_jobs: number
  profitable_jobs: number
  avg_margin_pct: number
  total_active_count: number
  valued_count: number
  is_forward_filled: boolean
}

const COLORS = {
  positive: CHART_COLORS.success,
  negative: CHART_COLORS.danger,
  flat: CHART_COLORS.muted,
  baseline: CHART_COLORS.baseline,
}

function colorFor(lastReal: Snapshot | undefined): string {
  if (!lastReal) return COLORS.flat
  if (lastReal.total_margin > 0) return COLORS.positive
  if (lastReal.total_margin < 0) return COLORS.negative
  return COLORS.flat
}

export function MarginHistoryChart() {
  const [period, setPeriod] = useState<Period>(30)
  const { key: forceRefetchKey, bump } = useForceRefetch()
  const qc = useQueryClient()

  const { data, isLoading, isFetching, dataUpdatedAt, error } = useQuery({
    queryKey: ['margin-history', period, forceRefetchKey],
    queryFn: () => api.industryMarginHistory(period),
  })

  const snapshot = useMutation({
    mutationFn: () => api.industryMarginSnapshot(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['margin-history'] }),
  })

  const snapshots: Snapshot[] = data?.snapshots ?? []
  const realCount = snapshots.filter((s) => !s.is_forward_filled).length
  const lastReal = [...snapshots].reverse().find((s) => !s.is_forward_filled)
  const stroke = colorFor(lastReal)

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Margin history</h2>
          <p className="text-xs text-eve-muted">
            Total margin (ISK) de tus jobs manufacturables activos, por día.
            {realCount > 0 && (
              <>
                {' '}Último snapshot:{' '}
                <span className="font-mono">
                  {lastReal?.date} · {formatISK(lastReal?.total_margin ?? 0)} ISK
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodToggle value={period} onChange={setPeriod} disabled={isFetching} />
          <button
            onClick={() => snapshot.mutate()}
            disabled={snapshot.isPending}
            className="text-xs px-3 py-1.5 rounded-md border bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text hover:border-eve-accent/40 transition-colors disabled:opacity-50"
            title="Capturar el estado actual del char en D1 (idempotente por día)"
          >
            {snapshot.isPending ? 'Snapshot…' : 'Snapshot now'}
          </button>
          <button
            onClick={bump}
            disabled={isFetching}
            className="text-xs px-3 py-1.5 rounded-md border bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text hover:border-eve-accent/40 transition-colors disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {snapshot.isSuccess && (
        <div className="text-xs text-eve-success mb-2 italic">
          Snapshot guardado: {snapshot.data.snapshot_date} ·{' '}
          {formatISK(snapshot.data.total_margin)} ISK
        </div>
      )}
      {snapshot.isError && (
        <div className="text-xs text-eve-danger mb-2 italic">
          Snapshot falló: {(snapshot.error as Error).message}
        </div>
      )}

      {isLoading ? (
        <MarginHistorySkeleton />
      ) : error ? (
        <div className="text-eve-danger italic py-8 text-center text-sm">
          Error cargando margin history.
        </div>
      ) : snapshots.length < 2 ? (
        <EmptyState snapshots={snapshots} />
      ) : (
        <>
          <ChartSvg snapshots={snapshots} stroke={stroke} />
          <ChartLegend
            snapshots={snapshots}
            dataUpdatedAt={dataUpdatedAt}
          />
        </>
      )}
    </div>
  )
}

function PeriodToggle({
  value,
  onChange,
  disabled,
}: {
  value: Period
  onChange: (v: Period) => void
  disabled?: boolean
}) {
  const opts: Period[] = [7, 30, 90]
  return (
    <div className="flex border border-eve-border rounded-md overflow-hidden">
      {opts.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          disabled={disabled}
          className={`text-xs px-2.5 py-1.5 transition-colors ${
            value === d
              ? 'bg-eve-accent/10 text-eve-accent'
              : 'bg-eve-panel text-eve-muted hover:text-eve-text'
          } ${disabled ? 'opacity-50' : ''}`}
        >
          {d}d
        </button>
      ))}
    </div>
  )
}

function ChartSvg({
  snapshots,
  stroke,
}: {
  snapshots: Snapshot[]
  stroke: string
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const width = 720
  const height = 160
  const padL = 56
  const padR = 12
  const padT = 12
  const padB = 24
  const innerW = width - padL - padR
  const innerH = height - padT - padB

  const values = snapshots.map((s) => s.total_margin)
  const minVal = Math.min(0, ...values)
  const maxVal = Math.max(0, ...values)
  const range = maxVal - minVal || 1

  const xAt = (i: number) =>
    padL + (snapshots.length === 1 ? innerW / 2 : (i / (snapshots.length - 1)) * innerW)
  const yAt = (v: number) => padT + (1 - (v - minVal) / range) * innerH
  const yZero = yAt(0)

  const linePath = snapshots
    .map((s, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(2)} ${yAt(s.total_margin).toFixed(2)}`)
    .join(' ')

  const areaPath =
    `${linePath} L ${xAt(snapshots.length - 1).toFixed(2)} ${yZero}` +
    ` L ${xAt(0).toFixed(2)} ${yZero} Z`

  // Tick values: divide el Y range en 4 steps, incluyendo el 0 si aplica
  const ticks: number[] = []
  for (let i = 0; i <= 4; i++) {
    ticks.push(minVal + (range * i) / 4)
  }

  // Hover — track nearest index por X.
  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const xRatio = (e.clientX - rect.left) / rect.width
    const xInViewBox = xRatio * width
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < snapshots.length; i++) {
      const d = Math.abs(xAt(i) - xInViewBox)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    setHoverIdx(bestIdx)
  }

  const hoverSnap = hoverIdx !== null ? snapshots[hoverIdx] : null
  const hoverX = hoverIdx !== null ? xAt(hoverIdx) : 0
  const hoverY = hoverIdx !== null ? yAt(snapshots[hoverIdx].total_margin) : 0

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="overflow-visible block"
        role="img"
        aria-label={`Margin history con ${snapshots.length} puntos`}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Area fill */}
        <path d={areaPath} fill={stroke} fillOpacity={0.12} stroke="none" />

        {/* Y baseline (0 line) si hay valores negativos */}
        {minVal < 0 && (
          <line
            x1={padL}
            y1={yZero}
            x2={width - padR}
            y2={yZero}
            stroke={COLORS.baseline}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {/* Y ticks + labels */}
        {ticks.map((v, i) => (
          <g key={i}>
            <line
              x1={padL - 3}
              y1={yAt(v)}
              x2={padL}
              y2={yAt(v)}
              stroke={COLORS.baseline}
              strokeWidth={1}
            />
            <text
              x={padL - 6}
              y={yAt(v) + 3}
              fontSize={9}
              fill={COLORS.flat}
              textAnchor="end"
              fontFamily="monospace"
            >
              {formatISKShort(Math.round(v))}
            </text>
          </g>
        ))}

        {/* X axis: date labels (cada ~6 puntos para no saturar) */}
        {snapshots.map((s, i) => {
          const step = Math.max(1, Math.ceil(snapshots.length / 6))
          if (i % step !== 0 && i !== snapshots.length - 1) return null
          return (
            <text
              key={s.date}
              x={xAt(i)}
              y={height - 6}
              fontSize={9}
              fill={COLORS.flat}
              textAnchor="middle"
              fontFamily="monospace"
            >
              {s.date.slice(5)}
            </text>
          )
        })}

        {/* Línea */}
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Dots: real (full color) vs forward-filled (low opacity) */}
        {snapshots.map((s, i) => (
          <circle
            key={s.date}
            cx={xAt(i)}
            cy={yAt(s.total_margin)}
            r={s.is_forward_filled ? 1.8 : 3}
            fill={s.is_forward_filled ? COLORS.flat : stroke}
            fillOpacity={s.is_forward_filled ? 0.35 : 1}
            stroke={!s.is_forward_filled ? CHART_COLORS.bg : 'none'}
            strokeWidth={1}
          />
        ))}

        {/* Hover crosshair + dot (encima de todo lo demás) */}
        {hoverIdx !== null && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              y1={padT}
              x2={hoverX}
              y2={height - padB}
              stroke={COLORS.flat}
              strokeWidth={1}
              strokeOpacity={0.5}
              strokeDasharray="2 3"
            />
            <circle
              cx={hoverX}
              cy={hoverY}
              r={hoverSnap?.is_forward_filled ? 3 : 4}
              fill={hoverSnap?.is_forward_filled ? COLORS.flat : stroke}
              fillOpacity={hoverSnap?.is_forward_filled ? 0.6 : 1}
              stroke={CHART_COLORS.bg}
              strokeWidth={1.5}
            />
          </g>
        )}
      </svg>
      {hoverSnap && (
        <div
          className="absolute pointer-events-none bg-eve-panel border border-eve-border rounded-md px-2 py-1 text-[10px] font-mono shadow-lg whitespace-nowrap"
          style={{
            left: `${(hoverX / width) * 100}%`,
            top: `${(hoverY / height) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 10px))',
          }}
        >
          <div className="text-eve-muted flex items-center gap-1">
            <span>{hoverSnap.date}</span>
            {hoverSnap.is_forward_filled && (
              <span className="text-[9px] uppercase tracking-wider text-eve-warn">
                forward-fill
              </span>
            )}
          </div>
          <div
            className={`font-semibold ${
              hoverSnap.total_margin > 0
                ? 'text-eve-success'
                : hoverSnap.total_margin < 0
                  ? 'text-eve-danger'
                  : 'text-eve-text'
            }`}
          >
            {hoverSnap.total_margin > 0 ? '+' : ''}
            {formatISK(hoverSnap.total_margin)} ISK
          </div>
          {hoverSnap.profitable_jobs > 0 && (
            <div className="text-eve-muted text-[9px] mt-0.5">
              {hoverSnap.profitable_jobs}/{hoverSnap.marginable_jobs} rentables
              · avg {hoverSnap.avg_margin_pct}%
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ChartLegend({
  snapshots,
  dataUpdatedAt,
}: {
  snapshots: Snapshot[]
  dataUpdatedAt: number
}) {
  const real = snapshots.filter((s) => !s.is_forward_filled).length
  const filled = snapshots.length - real
  return (
    <div className="flex items-center justify-between mt-2 text-[10px] text-eve-muted flex-wrap gap-2">
      <div className="flex gap-3">
        <span>
          <span className="inline-block w-2 h-2 rounded-full bg-eve-success align-middle mr-1" />
          {real} snapshots reales
        </span>
        {filled > 0 && (
          <span>
            <span className="inline-block w-2 h-2 rounded-full bg-eve-muted opacity-40 align-middle mr-1" />
            {filled} forward-filled
          </span>
        )}
      </div>
      <span className="font-mono">
        refreshed {new Date(dataUpdatedAt).toLocaleTimeString()}
      </span>
    </div>
  )
}

function EmptyState({ snapshots }: { snapshots: Snapshot[] }) {
  return (
    <div className="text-center py-8 space-y-2">
      <div className="text-eve-muted text-sm">
        Sin snapshots todavía. El cron diario captura 1 user/día — click{' '}
        <span className="font-mono">Snapshot now</span> para empezar.
      </div>
      {snapshots.length === 1 && (
        <div className="text-eve-muted text-xs italic">
          1 punto en D1 ({snapshots[0].date},{' '}
          {formatISK(snapshots[0].total_margin)} ISK). Necesitamos ≥ 2 para
          graficar.
        </div>
      )}
    </div>
  )
}

/**
 * Skeleton que mimic la estructura del chart: area SVG + legend bar.
 * Mismo height que ChartSvg (160 viewBox) para evitar jump al cargar.
 */
function MarginHistorySkeleton() {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      <div className="bg-eve-bg border border-eve-border rounded-md h-[160px] flex items-end gap-1 p-3">
        {[0.3, 0.5, 0.7, 0.4, 0.6, 0.8, 0.45, 0.65, 0.55, 0.4].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-eve-border/40 rounded-t animate-pulse"
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="h-3 w-24 bg-eve-border/30 rounded animate-pulse" />
        <div className="h-3 w-32 bg-eve-border/30 rounded animate-pulse" />
      </div>
    </div>
  )
}
